import React, { useCallback, useLayoutEffect } from 'react';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useCart } from '../../hooks';
import { Customization } from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const CustomizationEditScreen = () => {
  const navigation =
    useSafeNavigation<NativeStackNavigationProp<ParamListBase>>();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const routeParams = useSafeRouteParams<CustomizationEditScreenRouteParams>();

  const { restaurantSlug, productSlug, lineItemId } = routeParams;

  const { cart } = useCart();

  // ─────────────────────────────────────────────────────────────────

  const navigateToBag = useCallback(() => {
    if (isExpoRouterEnabled) {
      try {
        router?.dismissTo('/bag');
      } catch {
        // expo-router throws if called before Root Layout mounts
      }

      return;
    }

    const previousRoutes = navigation?.getState().routes;
    const previousRoute = previousRoutes?.at(-2);

    // When navigating to the bag, if the bag is already in the stack, just return to it.
    if (navigation?.canGoBack() && previousRoute?.name === 'Bag') {
      navigation.goBack();
      return;
    }

    navigation?.replace('Bag');
  }, [isExpoRouterEnabled, navigation, router]);

  const navigateToCustomizationScreen = useCallback(() => {
    if (isExpoRouterEnabled) {
      try {
        router?.replace({
          pathname: '/[restaurantSlug]/[productSlug]',
          params: {
            restaurantSlug: restaurantSlug ?? '',
            productSlug: productSlug ?? '',
          },
        });
      } catch {
        // expo-router throws if called before Root Layout mounts
      }

      return;
    }

    navigation?.replace('Customization', { restaurantSlug, productSlug });
  }, [isExpoRouterEnabled, navigation, router, productSlug, restaurantSlug]);

  // ─────────────────────────────────────────────────────────────────

  // redirect to the customization screen if the line item isn't in the cart or has been removed
  useLayoutEffect(() => {
    const lineItems = cart?.lineItems;
    const isLineItemInCart = lineItems?.some(({ id }) => id === lineItemId);

    if (isLineItemInCart) return;

    navigateToCustomizationScreen();
  }, [cart?.lineItems, lineItemId, navigateToCustomizationScreen]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Customization
      restaurantSlug={restaurantSlug}
      productSlug={productSlug}
      lineItemId={lineItemId}
      isEditProductMode={true}
      onEditLineItemInCart={navigateToBag}
      onRemoveLineItemFromCart={navigateToBag}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationEditScreenRouteParams = {
  restaurantSlug: string;
  productSlug: string;
  lineItemId: string;
};
